import type {DesignModuleSpecJson} from '../data/contracts';
import {evaluateRowFormula,parseFormula,RECORD_INSTANT} from '@/uptk/core/components/dashboard/data/formula';
import {displayTokens} from './measuredCatalog';

/**
 * K-PLAN (29.10.2026) — « Proposer les champs calculés » : les valeurs qu'un prototype calcule en rendant une collection (script de
 * rendu « items.map(item => { const a = …, b = …; return `…${a}…` }) ») deviennent des champs calculés du modèle, proposés au Maker
 * (jamais appliqués sans sa coche), chacun prouvé par les valeurs affichées des captures du prototype vivant :
 * - lecture bornée du script, sans eval ni exécution : expressions arithmétiques sur les propriétés de l'élément, Math.max / min /
 *   abs / round, fonctions de mise en forme à un argument (ignorées), différence de mois entiers entre une date de l'élément et la date
 *   du jour (→ months_until), « c ? x : null » quand x divise par la grandeur que c teste ;
 * - propriétés de l'élément rattachées aux champs du modèle comme l'extraction de la simulation (nom exact, ou suffixe amount / payment
 *   / contribution retiré) ;
 * - champ cible : un champ numérique non calculé dont le nom commence par le nom de la variable du script (remaining → remainingamount),
 *   un champ de pourcentage pour une expression « … * 100 » affichée suivie de « % », sinon un champ nouveau nommé d'après la variable
 *   et titré par le libellé affiché devant la valeur ;
 * - preuve : pour chaque ligne de la simulation, la formule calculée (à l'instant de la capture pour months_until) figure dans la carte
 *   de cette ligne dans une capture (texte entre son libellé et celui de la ligne suivante). Toute ligne non retrouvée ou discordante
 *   écarte la proposition (au moins deux lignes prouvées).
 * Générique : aucun module, objet ni champ nommés.
 */
export type ComputedProof={record:string;expected:number;shown:string};
export type ComputedFieldProposal={entity:string;field:string;/** Fields of other proposals the formula reads : applied only with them. */requires:string[];create?:{type:'amount'|'number';label:Record<string,string>};formula:string;scale:number;serverOnly:boolean;source:string;proofs:ComputedProof[];/** A state : its labels in order (state n = labels[n-1]), shown by the prototype. */labels?:string[];/** K-PLAN : the shorter nested form of a state chain, identical on every row of the prototype. */simplified?:true};
type Field={name:string;type?:string;label?:unknown;computed?:unknown;protection?:{personalData?:boolean}};
type Entities=Record<string,{fields:Field[]}>;
type Rows=Record<string,Record<string,unknown>[]>;

const key=(value:string)=>value.normalize('NFD').replace(/\p{M}/gu,'').toLowerCase().replace(/[^a-z0-9]/g,'').replace(/s$/,'');
const NUMERIC=new Set(['amount','number','integer','decimal','percentage']);

/** The end of a bracketed / template region starting at `start` (the opening character), strings and templates skipped. */
function matching(source:string,start:number):number{
 const open=source[start],close=open==='('?')':open==='{'?'}':open==='['?']':'';
 let depth=0;
 for(let index=start;index<source.length;index++){
  const char=source[index];
  if(char==='\''||char==='"'){const end=source.indexOf(char,index+1);if(end<0)return -1;index=end;continue;}
  if(char==='`'){index=templateEnd(source,index);if(index<0)return -1;continue;}
  if(char===open)depth++;else if(char===close){depth--;if(!depth)return index;}
 }
 return -1;
}
/** End index (the closing backtick) of the template literal opening at `start`. */
function templateEnd(source:string,start:number):number{
 for(let index=start+1;index<source.length;index++){
  const char=source[index];
  if(char==='\\'){index++;continue;}
  if(char==='`')return index;
  if(char==='$'&&source[index+1]==='{'){const end=matching(source,index+1);if(end<0)return -1;index=end;}
 }
 return -1;
}
/** Top-level `${…}` of a template literal, with the visible text before (since the last tag) and after (until the next tag). */
function interpolations(template:string):{expr:string;before:string;after:string}[]{
 const out:{expr:string;before:string;after:string}[]=[];
 const text=(value:string)=>value.replace(/<[^>]*>/g,' ').replace(/\$\{[\s\S]*?\}/g,' ').replace(/\s+/g,' ').trim();
 let last=0;
 for(let index=0;index<template.length;index++){
  if(template[index]==='$'&&template[index+1]==='{'){
   const end=matching(template,index+1);if(end<0)break;
   const expr=template.slice(index+2,end),before=template.slice(last,index);
   const rest=template.slice(end+1),stop=rest.search(/<|\$\{/);
   const chunks=before.split(/<[^>]*>/).map(text).filter(Boolean);
   out.push({expr,before:chunks[chunks.length-1]??'',after:text(stop<0?rest:rest.slice(0,stop))});
   last=end+1;index=end;
  }
 }
 return out;
}
type Render={collection:string;item:string;decls:Map<string,string>;template:string};
/** Collection renderings : `<path>.map((item[,i])=>{const …;return `…`})` and `<path>.map(item=>`…`)`. */
export function collectionRenderings(source:string):Render[]{
 const out:Render[]=[];
 if(source.length>6*1024*1024)return out;
 for(const match of source.matchAll(/([A-Za-z_$][\w$.]*)\.map\(\s*\(?\s*([A-Za-z_$][\w$]*)\s*(?:,\s*[A-Za-z_$][\w$]*\s*)?\)?\s*=>\s*/g)){
  const start=match.index!+match[0].length,collection=match[1].split('.').pop()!,item=match[2];
  if(source[start]==='`'){const end=templateEnd(source,start);if(end>0)out.push({collection,item,decls:new Map(),template:source.slice(start+1,end)});continue;}
  if(source[start]!=='{')continue;
  const end=matching(source,start);if(end<0||end-start>20000)continue;
  const body=source.slice(start+1,end);
  const decl=/^\s*const\s+/.exec(body),ret=body.search(/return\s+`/);
  if(!decl||ret<0)continue;
  const declarations=body.slice(decl[0].length,body.lastIndexOf(';',ret));
  const decls=new Map<string,string>();
  // Top-level commas of the declaration list.
  let depth=0,from=0;const parts:string[]=[];
  for(let index=0;index<declarations.length;index++){const char=declarations[index];if('([{'.includes(char))depth++;else if(')]}'.includes(char))depth--;else if(char==='\''||char==='"'){index=declarations.indexOf(char,index+1);if(index<0)break;}else if(char===','&&!depth){parts.push(declarations.slice(from,index));from=index+1;}}
  parts.push(declarations.slice(from));
  for(const part of parts){const assignment=/^\s*([A-Za-z_$][\w$]*)\s*=\s*([\s\S]+)$/.exec(part);if(assignment)decls.set(assignment[1],assignment[2].trim());}
  const templateStart=body.indexOf('`',ret),templateStop=templateEnd(body,templateStart);
  if(templateStop>0)out.push({collection,item,decls,template:body.slice(templateStart+1,templateStop)});
 }
 return out;
}

/** JS expression → formula of the model (or null when any part is unsupported). `props` maps item properties to fields. */
type Translation={formula:string;operands:Set<string>}|{labels:string[];formula:string;operands:Set<string>;additive?:string};
const MONTHS=/\(\s*([A-Za-z_$][\w$]*)\.getFullYear\(\)\s*-\s*([A-Za-z_$][\w$]*)\.getFullYear\(\)\s*\)\s*\*\s*12\s*\+\s*\1\.getMonth\(\)\s*-\s*\2\.getMonth\(\)\s*(?:-\s*\(\s*\1\.getDate\(\)\s*<\s*\2\.getDate\(\)\s*\?\s*1\s*:\s*0\s*\))?/g;
function translate(expr:string,render:Render,props:(prop:string)=>string|null,depth=0,alias:Map<string,string>=new Map()):Translation|null{
 if(depth>8||expr.length>2000)return null;
 const operands=new Set<string>();
 let source=expr.trim();
 // Whole months from today to a date of the item : `due=new Date(item.date…)` then the year/month difference.
 source=source.replace(MONTHS,(whole,later:string,earlier:string)=>{
  const declared=render.decls.get(later)??'';const date=new RegExp('^new\\s+Date\\(\\s*'+render.item+'\\.([A-Za-z_$][\\w$]*)').exec(declared);
  const field=date?props(date[1]):null;
  if(!field||render.decls.has(earlier)&&!/new\s+Date\(\s*\)/.test(render.decls.get(earlier)!))return whole;
  operands.add(field);return '§months_until('+field+')§';
 });
 // String-label ternary chains : c1 ? 'A' : c2 ? 'B' : 'C' (labels of a state).
 const tokens=tokenize(source);if(!tokens)return null;
 let at=0;
 const peek=()=>tokens[at],next=()=>tokens[at++];
 type Node={kind:'num';value:number}|{kind:'ref';name:string}|{kind:'raw';text:string}|{kind:'bin';op:string;left:Node;right:Node}|{kind:'call';name:string;args:Node[]}|{kind:'neg';value:Node}|{kind:'str';value:string}|{kind:'null'}|{kind:'tern';test:Node;yes:Node;no:Node};
 const primary=():Node|null=>{
  const token=next();if(!token)return null;
  if(token.type==='num')return {kind:'num',value:token.value};
  if(token.type==='str')return {kind:'str',value:token.value};
  if(token.type==='raw')return {kind:'raw',text:token.value};
  if(token.type==='op'&&token.value==='('){const inner=ternary();if(next()?.value!==')')return null;return inner;}
  if(token.type==='op'&&token.value==='-'){const value=unary();return value?{kind:'neg',value}:null;}
  if(token.type==='id'){
   if(token.value==='null'||token.value==='undefined')return {kind:'null'};
   if(peek()?.value==='('){
    next();const args:Node[]=[];
    if(peek()?.value!==')')do{const arg=ternary();if(!arg)return null;args.push(arg);}while(peek()?.value===','&&next());
    if(next()?.value!==')')return null;
    return {kind:'call',name:token.value,args};
   }
   if(token.value.startsWith(render.item+'.')){const field=props(token.value.slice(render.item.length+1));if(!field)return null;operands.add(field);return {kind:'ref',name:field};}
   // A variable already proposed as a computed field is read from that field (shorter formulas, one definition).
   const aliased=alias.get(token.value);if(aliased){operands.add(aliased);return {kind:'ref',name:aliased};}
   const declared=render.decls.get(token.value);
   if(declared!==undefined){const inner=translate(declared,render,props,depth+1,alias);if(!inner||'labels' in inner)return null;inner.operands.forEach(item=>operands.add(item));return {kind:'raw',text:inner.formula};}
   return null;
  }
  return null;
 };
 const unary=():Node|null=>primary();
 const binary=(ops:string[],lower:()=>Node|null)=>():Node|null=>{let left=lower();while(left&&peek()?.type==='op'&&ops.includes(String(peek()!.value))){const op=String(next()!.value);const right=lower();if(!right)return null;left={kind:'bin',op,left,right};}return left;};
 const multiplicative=binary(['*','/'],unary),additive=binary(['+','-'],multiplicative),comparison=binary(['===','==','!==','!=','>=','<=','>','<'],additive),logical=binary(['&&','||'],comparison);
 const ternary=():Node|null=>{const test=logical();if(!test)return null;if(peek()?.value!=='?')return test;next();const yes=ternary();if(!yes||next()?.value!==':')return null;const no=ternary();return no?{kind:'tern',test,yes,no}:null;};
 const root=ternary();if(!root||at!==tokens.length)return null;
 // Emit, with the fewest parentheses (formula bounds : 300 characters, depth 8).
 type Out={text:string;prec:number};
 const wrap=(out:Out,min:number)=>out.prec<min?'('+out.text+')':out.text;
 const numeric=(node:Node):Out|null=>{
  switch(node.kind){
   case 'num':return {text:String(node.value),prec:3};
   case 'ref':return {text:node.name,prec:3};
   case 'raw':{const text=node.text.replace(/§/g,'');return {text,prec:precedence(text)};}
   case 'neg':{const value=numeric(node.value);return value===null?null:{text:'-'+wrap(value,3),prec:2};}
   case 'bin':{
    if(!['+','-','*','/'].includes(node.op))return null;
    const left=numeric(node.left),right=numeric(node.right);if(!left||!right)return null;
    const prec=['+','-'].includes(node.op)?1:2;
    return {text:wrap(left,prec)+' '+node.op+' '+wrap(right,['-','/'].includes(node.op)?prec+1:prec),prec};
   }
   case 'call':{
    const name=node.name.replace(/^Math\./,'');
    if(['max','min','abs','round'].includes(name)&&node.name.startsWith('Math.')){const args=node.args.map(numeric);return args.some(arg=>arg===null)?null:{text:name+'('+args.map(arg=>arg!.text).join(', ')+')',prec:3};}
    // A one-argument formatting function (money, chf, number…) shows its argument.
    if(node.args.length===1&&!node.name.includes('.'))return numeric(node.args[0]);
    return null;
   }
   case 'tern':{
    // « c ? x : null » : x where x divides by the quantity the condition tests against 0 (x/0 is absent too).
    if(node.no.kind!=='null'||node.test.kind!=='bin'||!['>','!==','!='].includes(node.test.op)||node.test.right.kind!=='num'||node.test.right.value!==0)return null;
    const tested=numeric(node.test.left),value=numeric(node.yes);
    if(!tested||!value)return null;
    return value.text.endsWith('/ '+wrap(tested,3))||value.text.endsWith('/ '+tested.text)?value:null;
   }
   default:return null;
  }
 };
 // A comparison as 0 / 1 : step of the difference (resolution 10⁻⁶).
 const condition=(node:Node):string|null=>{
  if(node.kind!=='bin')return null;
  const left=numeric(node.left),right=numeric(node.right);if(!left||!right)return null;
  const diff=(a:Out,b:Out)=>b.text==='0'?a.text:wrap(a,1)+' - '+wrap(b,2);
  const step=(a:Out,b:Out)=>'min(1, max(0, ('+diff(a,b)+') * 1000000 + 1))';
  switch(node.op){
   case '>=':return step(left,right);case '<=':return step(right,left);
   case '>':return '(1 - '+step(right,left)+')';case '<':return '(1 - '+step(left,right)+')';
   case '===':case '==':return '(1 - min(1, abs('+diff(left,right)+') * 1000000))';
   case '!==':case '!=':return 'min(1, abs('+diff(left,right)+') * 1000000)';
   default:return null;
  }
 };
 // A chain of string labels : the state index (1 = first label), labels in order : c ? A : B = B - c * (B - A).
 const labels:string[]=[];
 const state=(node:Node):{text:string;value?:number}|null=>{
  if(node.kind==='str'){labels.push(node.value);return {text:String(labels.length),value:labels.length};}
  if(node.kind==='call'&&node.args.length>=1&&!node.name.includes('.'))return state(node.args[0]);
  if(node.kind!=='tern')return null;
  const test=condition(node.test);if(test===null)return null;
  const yes=state(node.yes),no=state(node.no);if(!yes||!no)return null;
  if(yes.value!==undefined&&no.value!==undefined){const gap=no.value-yes.value;return {text:no.value+' - '+(gap===1?'':gap+' * ')+test};}
  // The shorter of « B - c * (B - A) » and « B * (1 - c) + A * c » : the long branch is written once (the server inlines computed
  // operands in SQL ; its size is bounded).
  const repeated='('+no.text+') - '+test+' * ('+no.text+' - ('+yes.text+'))';
  const weighted='('+no.text+') * (1 - '+test+') + '+(yes.value===1?'':yes.value!==undefined?yes.value+' * ':'('+yes.text+') * ')+test;
  return {text:weighted.length<repeated.length?weighted:repeated};
 };
 const value=numeric(root);
 if(value!==null)return {formula:value.text,operands};
 const stateFormula=state(root);
 // A linear chain c1 ? L1 : c2 ? L2 : … : Ln also reads « n - c1 - c2 - … » when the conditions are nested (once true, all later true) :
 // far shorter in SQL (the server inlines computed operands, size-bounded). Kept only if it gives the same state on every row.
 const linear:string[]=[];
 for(let node:Node=root;;){
  if(node.kind==='call'&&node.args.length>=1&&!node.name.includes('.')){node=node.args[0];continue;}
  if(node.kind!=='tern'||node.yes.kind!=='str')break;
  const test=condition(node.test);if(test===null){linear.length=0;break;}
  linear.push(precedence(test)<=1?'('+test+')':test);node=node.no;
  if(node.kind==='str')break;
 }
 const nested=linear.length===labels.length-1&&linear.length>=1?labels.length+' - '+linear.join(' - '):undefined;
 return stateFormula!==null&&labels.length>=2&&new Set(labels).size===labels.length?{formula:stateFormula.text,labels,operands,...(nested?{additive:nested}:{})}:null;
}
/** Lowest operator precedence at depth 0 of a formula : 1 (+ -), 2 (* /), 3 (atom or call). */
function precedence(text:string):number{
 let depth=0,lowest=3;
 for(let index=0;index<text.length;index++){
  const char=text[index];
  if(char==='(')depth++;else if(char===')')depth--;
  else if(!depth&&index>0&&(char==='+'||char==='-')&&text[index-1]===' ')lowest=1;
  else if(!depth&&(char==='*'||char==='/'))lowest=Math.min(lowest,2);
 }
 return lowest;
}
type Token={type:'num';value:number}|{type:'str'|'id'|'op'|'raw';value:string};
function tokenize(source:string):Token[]|null{
 const out:Token[]=[];
 for(let index=0;index<source.length;){
  const char=source[index];
  if(/\s/.test(char)){index++;continue;}
  if(char==='§'){const end=source.indexOf('§',index+1);if(end<0)return null;out.push({type:'raw',value:source.slice(index+1,end)});index=end+1;continue;}
  const number=/^\d+(?:\.\d+)?/.exec(source.slice(index));if(number){out.push({type:'num',value:Number(number[0])});index+=number[0].length;continue;}
  if(char==='\''||char==='"'){const end=source.indexOf(char,index+1);if(end<0)return null;out.push({type:'str',value:source.slice(index+1,end)});index=end+1;continue;}
  const id=/^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*/.exec(source.slice(index));if(id){out.push({type:'id',value:id[0]});index+=id[0].length;continue;}
  const op=/^(===|!==|==|!=|>=|<=|&&|\|\||[-+*/()?:,<>])/.exec(source.slice(index));if(op){out.push({type:'op',value:op[0]});index+=op[0].length;continue;}
  return null;
 }
 return out;
}

/** Item properties → fields of an object, as the simulation's extraction reads them (exact name, else amount / payment suffix). */
function propertyMap(fields:Field[]){
 return (prop:string)=>{
  const target=key(prop);
  const exact=fields.filter(field=>key(field.name)===target);
  if(exact.length===1)return exact[0].name;
  const stem=fields.filter(field=>key(field.name.replace(/(amount|payment|contribution)$/i,''))===target);
  return stem.length===1?stem[0].name:null;
 };
}
const labelOf=(label:unknown)=>typeof label==='string'?label:label&&typeof label==='object'?String((label as Record<string,unknown>).fr??Object.values(label)[0]??''):'';
const plain=(html:string)=>html.replace(/<script[\s\S]*?<\/script>|<style[\s\S]*?<\/style>/gi,' ').replace(/<[^>]*>/g,' ').replace(/&nbsp;|&#(?:160|xa0);/gi,' ').replace(/&amp;/g,'&').replace(/&#39;|&apos;/g,'\'').replace(/&quot;/g,'"').replace(/\s+/g,' ');

export type ComputedEvidenceInput={spec:DesignModuleSpecJson;scripts:string[];captures:string[];rows:Rows;capturedAt:string};
export function proposeComputedFields(input:ComputedEvidenceInput):ComputedFieldProposal[]{
 const entities=(input.spec.entities??{}) as Entities,out:ComputedFieldProposal[]=[];
 const texts=input.captures.map(plain);
 for(const script of input.scripts)for(const render of collectionRenderings(script)){
  // The object the collection renders : its code ends with the collection's name (goals → financialgoal) — one object only.
  const owners=Object.keys(entities).filter(code=>key(code).endsWith(key(render.collection))&&key(render.collection).length>=3);
  if(owners.length!==1)continue;
  const entity=owners[0],fields=entities[entity].fields,props=propertyMap(fields),rows=input.rows[entity]??[];
  if(rows.length<2)continue;
  const display=fields.find(field=>['label','name','title'].includes(field.name))?.name;if(!display)continue;
  // Two passes : values first (a variable proposed as a field is then read from it), states (labels chosen by comparisons) next.
  const seen=new Set<string>(),alias=new Map<string,string>(),known:{field:string;formula:string}[]=[];
  const pieces=interpolations(render.template);
  for(const pass of ['values','states'] as const)for(const piece of pieces){
   const variable=/^\s*(?:[A-Za-z_$][\w$]*\()?\s*([A-Za-z_$][\w$]*)\s*\)?\s*$/.exec(piece.expr)?.[1];
   const declared=variable&&render.decls.has(variable)?variable:undefined;
   const translation=translate(piece.expr,render,props,0,pass==='states'?alias:new Map());
   if(!translation||!translation.operands.size||('labels' in translation)!==(pass==='states'))continue;
   // A plain field of the item shown as is is a binding, not a calculation.
   if(!('labels' in translation)&&/^[a-z][a-z0-9_]*$/.test(translation.formula))continue;
   // Target : an existing numeric field named by the script's variable, a percentage field for « … * 100 % », else a new field.
   const free=fields.filter(field=>!field.computed&&NUMERIC.has(field.type??'')&&!translation.operands.has(field.name));
   let target=declared?free.filter(field=>field.name.toLowerCase().startsWith(declared.toLowerCase())&&declared.length>=4):[];
   if(!declared&&!('labels' in translation)&&/\*\s*100\)?$/.test(translation.formula)&&/^%/.test(piece.after))target=free.filter(field=>/(progress|rate|ratio|percent|pct|share|taux|coverage)/.test(field.name));
   const statusField=fields.find(field=>field.type==='text'&&/^(status|state|statut|etat)$/.test(field.name));
   const label=piece.before.replace(/\s*[:·]\s*$/,'').trim()||('labels' in translation&&statusField?labelOf(statusField.label):'');
   let name:string,create:ComputedFieldProposal['create'];
   if(target.length===1)name=target[0].name;
   else if(target.length===0&&(declared||'labels' in translation)&&label&&label.length<=80){
    const base=(declared??statusField?.name??'state').toLowerCase().replace(/[^a-z0-9]/g,'');
    name=base+('labels' in translation?'code':'');
    if(fields.some(field=>field.name===name)||!/^[a-z][a-z0-9_]{1,40}$/.test(name))continue;
    create={type:'labels' in translation?'number':[...translation.operands].every(operand=>fields.find(field=>field.name===operand)?.type==='amount'||/months_until/.test(translation.formula))&&!/months_until/.test(translation.formula)?'amount':'number',label:{fr:label}};
   }else continue;
   if(seen.has(name))continue;
   try{parseFormula(translation.formula,'row');}catch{continue;}
   // Proof by the values the captures show for each row.
   const proofs:ComputedProof[]=[];let refuted=false;
   const labelsOf=rows.map(row=>String(row[display]??''));
   for(const [index,row] of rows.entries()){
    const own=labelsOf[index];if(!own)continue;
    let value:number|null;
    // Values of the fields proposed before (a state reads them), then the candidate, at the capture's instant.
    const values:Record<string,unknown>={...row,[RECORD_INSTANT]:input.capturedAt};
    try{for(const earlier of known)values[earlier.field]=evaluateRowFormula(earlier.formula,values,'absent',4);value=evaluateRowFormula(translation.formula,values,'absent',4);}catch{refuted=true;break;}
    if(value===null)continue;
    let found:string|undefined,searched=false;
    for(const text of texts){
     const from=text.indexOf(own);if(from<0)continue;searched=true;
     const stops=labelsOf.filter((other,at)=>at!==index&&other).map(other=>text.indexOf(other,from+own.length)).filter(position=>position>from);
     const segment=text.slice(from,stops.length?Math.min(...stops):from+1200);
     if('labels' in translation){const shown=translation.labels[Math.round(value)-1];if(shown&&segment.includes(shown)){found=shown;break;}}
     // eslint-disable-next-line no-restricted-properties -- i18n-exempt: arrondi de comparaison à une valeur lue dans la capture, jamais affiché
     else{const token=displayTokens(segment).numbers.find(item=>[0,1,2].some(decimals=>Math.abs(Number(value!.toFixed(decimals))-item.value)<1e-9&&item.decimals<=decimals));if(token){found=token.raw;break;}}
    }
    if(!searched)continue;
    if(!found){refuted=true;break;}
    proofs.push({record:own,expected:value,shown:found});
   }
   if(refuted||proofs.length<2)continue;
   // The nested form of a state, when shorter and identical on every row of the prototype (the full chain otherwise).
   let simplified=false;
   if('labels' in translation&&translation.additive&&translation.additive.length<translation.formula.length){
    const additive=translation.additive;let same=true;
    for(const row of rows){
     const values:Record<string,unknown>={...row,[RECORD_INSTANT]:input.capturedAt};
     try{for(const earlier of known)values[earlier.field]=evaluateRowFormula(earlier.formula,values,'absent',4);if(evaluateRowFormula(additive,values,'absent',4)!==evaluateRowFormula(translation.formula,values,'absent',4)){same=false;break;}}catch{same=false;break;}
    }
    if(same){translation.formula=additive;simplified=true;}
   }
   seen.add(name);known.push({field:name,formula:translation.formula});if(declared)alias.set(declared,name);
   const personal=[...translation.operands].some(operand=>fields.find(field=>field.name===operand)?.protection?.personalData);
   const requires=known.filter(earlier=>earlier.field!==name&&new RegExp('(^|[^a-z0-9_])'+earlier.field+'($|[^a-z0-9_])').test(translation.formula)).map(earlier=>earlier.field);
   out.push({entity,field:name,requires,...(create?{create}:{}),formula:translation.formula,scale:'labels' in translation||/months_until/.test(translation.formula)&&!/\//.test(translation.formula)?0:Number.isInteger(proofs[0].expected)&&proofs.every(proof=>Number.isInteger(proof.expected))?0:2,serverOnly:personal,source:piece.expr.slice(0,200),proofs,...('labels' in translation?{labels:[...translation.labels]}:{}),...(simplified?{simplified:true as const}:{})});
  }
 }
 return out;
}
export {labelOf as computedLabelOf};
